<template>
  <SmDialog
    v-model:visibility="localShowDrawer"
    :title="
      i18n.t('drawers.applicationDetailDrawer.header.softwareEdit', {
        softwareName: props.application.display_name,
      })
    ">
    <el-tabs v-model="userMode" @tab-change="checkIfUserTabIsShown">
      <el-tab-pane
        v-if="
          existingRequirementComponents.includes(
            (
              (props.application.type || '') + props.application.software_name
            ).toLowerCase()
          )
        "
        :label="
          i18n.t('drawers.applicationDetailDrawer.tabs.softwareInformation', {
            softwareName: props.application.display_name,
          })
        "
        name="SoftwareInformation"
        lazy>
        <ApplicationDetailTab :application-data="props.application" />
      </el-tab-pane>
      <el-tab-pane
        :label="
          i18n.t('drawers.applicationDetailDrawer.tabs.softwareEdit', {
            softwareName: props.application.display_name,
          })
        "
        name="EditSoftware"
        lazy>
        <ApplicationForm
          :application-data="application"
          @cancel="localShowDrawer = false">
          <slot name="application-edit-items" />
        </ApplicationForm>
      </el-tab-pane>

      <el-tab-pane
        v-if="application.information.sso_provider"
        name="applications"
        :label="i18n.t('applications')">
        <h4>{{ i18n.t('displayedApplications') }}:</h4>
        <div style="height: 1rem" />
        <SmTable
          ref="ssoAppTable"
          selectable
          :columns="[
            {
              width: 1,
              key: 'name',
              loader: true,
              label: i18n.t('general.name'),
            },
          ]"
          :loading="!subAccountsInitialized"
          :data="allSubAccounts"
          key-field="id">
          <template #name="{ row }">
            <div
              class="sub-account-select-names"
              @click="changeSebaccountSelected(row)">
              <ApplicationIcon :application-name="row.name" />
              {{ row.name }}
            </div>
          </template>
        </SmTable>
      </el-tab-pane>

      <!-- Groups -->
      <el-tab-pane
        v-if="
          application.licence_groups &&
          Object.keys(application.licence_groups).length !== 0
        "
        :label="i18n.t('groups')"
        name="Groups"
        :lazy="true">
        <SmInput
          v-model="search"
          label="Search"
          size="medium"
          outline
          style="margin: 10px; width: initial" />
        <ApplicationDetailGroupTab
          :application="props.application"
          :searchterm="search" />
      </el-tab-pane>
    </el-tabs>

    <!-- Slots -->

    <template #footer>
      <SmButton
        v-if="localTab === 'applications'"
        type="primary"
        :loading="loadingSubAccountSelection"
        class="ml-auto mt-4 block"
        @click="handleSubAccountSelection">
        {{ i18n.t('general.save') }}
      </SmButton>
    </template>
    <template #close>
      <div style="display: flex; align-items: center; gap: 0.8rem">
        <SmButtonClose @close="localShowDrawer = false" />
      </div>
    </template>
  </SmDialog>
</template>

<script setup lang="ts">
  import { TabPaneName } from 'element-plus'

  import { useI18n } from 'vue-i18n'

  import { SoftwareOut, SoftwareService, SubAccountAvailable } from '@/client'

  import SmTable from '@/components/sm/SmTable.vue'
  import { ComponentExposed } from 'vue-component-type-helpers'
  import ApplicationDetailGroupTab from './tabs/ApplicationDetailGroupTab.vue'
  import ApplicationDetailTab from './tabs/ApplicationDetailTab.vue'
  import { sendToast } from '@/components/sm/SmNotification'

  // Define Props and Emits
  const emit = defineEmits([
    'update:showLicenceView',
    'update:showDrawer',
    'update:tab',
    'update:licences',
    'userSaved',
    'closeDrawer',
    'applicationChanged',
    'licenses-changed',
  ])

  const props = withDefaults(
    defineProps<{
      application: SoftwareOut
      showDrawer: boolean
      editMode?: boolean
      tab: string
      selectedSubAccountId: string | null
    }>(),
    {
      showDrawer: false,
      editMode: true,
      tab: 'EditSoftware',
      selectedSubAccountId: null,
    }
  )

  const i18n = useI18n()

  const ssoAppTable =
    ref<ComponentExposed<typeof SmTable<SubAccountAvailable>>>()
  const allSubAccounts = ref<SubAccountAvailable[]>([])
  const subAccountsInitialized = ref(false)
  const loadingSubAccountSelection = ref(false)

  const localShowDrawer = computed({
    get: () => props.showDrawer,
    set: (value) => emit('update:showDrawer', value),
  })

  const pages = import.meta.glob(
    '/src/components/BaseApplicationDetails/modules/**.vue'
  )

  const existingRequirementComponents = Object.keys(pages).map((key) => {
    return (
      key.match(
        '\\/src\\/components\\/BaseApplicationDetails\\/modules\\/(.*)\\.vue'
      ) as RegExpMatchArray
    )[1].toLowerCase()
  })

  const search = ref('')

  const localTab = computed({
    get: () => props.tab,
    set: (value) => emit('update:tab', value),
  })

  // ref to check, if the user tab was selected,
  // if it was selected at least once, then set the
  // prop "was-already-selected" of "ApplicationDetailsSelectUserTab"
  //
  // --> This is needed, to tell the UserTab Component
  // when to render the users (if the users get rendered instantly,
  // the drawer begins to delay, so we now render the users,
  // when the tab was selected)
  //
  // Set the value with a timeout, because otherwise the el-tabs
  // animation starts lagging
  const userTabWasSelected = ref(false)

  function handleSubAccountSelection() {
    loadingSubAccountSelection.value = true
    if (!subAccountsInitialized.value) return
    let new_filter = []
    if (props.application.config.sub_accounts_filter_is_whitelist) {
      new_filter = ssoAppTable.value!.selection.map((s) => s.id)
    } else {
      const selected = ssoAppTable.value!.selection.map((s) => s.id)
      new_filter = allSubAccounts.value
        .map((s) => s.id)
        .filter((s) => !selected.includes(s))
    }
    SoftwareService.putSoftwareApiV1SoftwareSoftwareSoftwareIdPut({
      softwareId: props.application._id,
      requestBody: {
        config: {
          sub_accounts_filter: new_filter,
        },
      },
    }).then(() => {
      loadingSubAccountSelection.value = false
      sendToast(i18n.t('successfullySaved'), undefined, 'success')
      emit('applicationChanged')
    })
  }

  function changeSebaccountSelected(row: SubAccountAvailable) {
    if (!subAccountsInitialized.value) return
    ssoAppTable.value!.selectById(row.id)
  }

  function initializeSubAccounts() {
    if (!props.application.information.sso_provider) return
    if (!ssoAppTable.value) return

    subAccountsInitialized.value = false
    allSubAccounts.value = props.application.sub_accounts_available

    nextTick(() => {
      allSubAccounts.value.forEach((subAccount) => {
        const isInFilter =
          props.application.config.sub_accounts_filter.includes(subAccount.id)
        if (
          (props.application.config.sub_accounts_filter_is_whitelist &&
            isInFilter) ||
          (!props.application.config.sub_accounts_filter_is_whitelist &&
            !isInFilter)
        ) {
          ssoAppTable.value?.selectById(subAccount.id)
        }
      })

      subAccountsInitialized.value = true
    })
  }

  function checkIfUserTabIsShown(name: TabPaneName) {
    localTab.value = name.toString()

    if (name == 'Select') {
      setTimeout(() => {
        userTabWasSelected.value = true
      }, 20)
      return
    }
    userTabWasSelected.value = false
  }

  // User Mode
  const userMode = ref(props.tab)
  const dialogShown = ref(false)

  function closeDrawer() {
    if (userMode.value === 'EditSoftware') {
      emit('userSaved')
      dialogShown.value = true
    } else {
      localShowDrawer.value = false
    }
  }

  // When parent changes
  watch(
    () => props.tab,
    (value) => {
      userMode.value = value
      localTab.value = value
    }
  )

  watch(() => ssoAppTable.value, initializeSubAccounts)
  watch(() => props.application.sub_accounts_available, initializeSubAccounts, {
    deep: true,
  })
</script>

<!-- Needs to be global becaus its teleported -->
<style lang="scss">
  .sub-account-select-names {
    display: flex;
    align-items: center;
    gap: 1rem;
  }
</style>
